import React, {
  memo,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const MIN_WEEKS = 2;
const MAX_WEEKS = 4;
const DEFAULT_WEEKS = 4;
const DEFAULT_SALES_TREND_FACTOR = 2.0;
const DEFAULT_SLOW_SALE_FACTOR = 10.0;
const DEFAULT_TO_ORDER_FACTOR = 0.5;

const SALES_TRENDS_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.summary,
    },
    warning: {
      main: '#f57c00',
    },
    success: {
      main: '#2e7d32',
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(0,151,167,0.16), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const FILTER_SUMMARY_ITEM_SX = {
  minWidth: 0,
  px: 1,
  py: 0.75,
  borderRadius: 2,
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: 'rgba(0,151,167,0.05)',
};

const TEXT_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 320 },
  flex: { sm: '0 0 auto' },
};

const SMALL_NUMBER_FIELD_SX = {
  width: { xs: '100%', sm: 110 },
  flex: { sm: '0 0 auto' },
};

const TABLE_HEADER_SX = {
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
  fontWeight: 700,
};

const QUANTITY_INPUT_SX = {
  width: 92,
  minHeight: 36,
  px: 1.25,
  borderRadius: 1.5,
  border: '1px solid rgba(0,0,0,0.23)',
  font: 'inherit',
  color: 'inherit',
  backgroundColor: '#fff',
  '&:focus': {
    outline: `2px solid ${pageColors.summary}33`,
    borderColor: pageColors.summary,
  },
};

const FLAG_PILL_EMPTY_SX = {
  color: 'text.secondary',
  fontSize: '0.875rem',
  lineHeight: 1,
};

const FLAG_PILL_SX = {
  error: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#c62828',
    backgroundColor: 'rgba(211,47,47,0.12)',
    border: '1px solid rgba(211,47,47,0.18)',
  },
  primary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#1565c0',
    backgroundColor: 'rgba(21,101,192,0.12)',
    border: '1px solid rgba(21,101,192,0.18)',
  },
  warning: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#ef6c00',
    backgroundColor: 'rgba(245,124,0,0.14)',
    border: '1px solid rgba(239,108,0,0.18)',
  },
};

const TABLE_ROW_HOVER_SX = {
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

function formatQuantity(value) {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const quantity = Number(value);
  if (!Number.isFinite(quantity)) {
    return String(value);
  }

  return Number.isInteger(quantity) ? quantity.toLocaleString() : quantity.toFixed(2);
}

function parseNumericInput(value) {
  const trimmedValue = String(value ?? '').trim();

  if (!trimmedValue) {
    return null;
  }

  const numericValue = Number(trimmedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function validateWeeksInput(value) {
  const numericValue = parseNumericInput(value);

  if (
    numericValue === null ||
    !Number.isInteger(numericValue) ||
    numericValue < MIN_WEEKS ||
    numericValue > MAX_WEEKS
  ) {
    return {
      value: null,
      error: `Enter a whole number between ${MIN_WEEKS} and ${MAX_WEEKS}.`,
    };
  }

  return { value: numericValue, error: '' };
}

function validateThresholdInput(value, label) {
  const numericValue = parseNumericInput(value);

  if (numericValue === null || numericValue < 0.1) {
    return { value: null, error: `${label} must be 0.1 or higher.` };
  }

  return { value: numericValue, error: '' };
}

function getTrendSignals(trend, salesTrendFactor, slowSaleFactor, toOrderFactor) {
  const lastWeek = Number(trend?.lastWeekQuantity) || 0;
  const avgWeekly = Number(trend?.averageWeeklyQuantity) || 0;
  const availableStock = Number(trend?.availableStock) || 0;
  const unit = trend?.unit ?? '';

  return {
    lastWeek,
    avgWeekly,
    availableStock,
    unit,
    isFastSale:
      lastWeek >= salesTrendFactor * avgWeekly && !(lastWeek === 0 && avgWeekly === 0),
    isSlowSale: availableStock >= slowSaleFactor * avgWeekly && availableStock > 0,
    isToOrder: avgWeekly > toOrderFactor * availableStock,
  };
}

function buildTrendRow(trend, index, salesTrendFactor, slowSaleFactor, toOrderFactor) {
  const articleNumber = String(trend?.articleNumber ?? '');
  const articleDescription = String(trend?.articleDescription ?? '');
  const supplierName = String(trend?.supplierName ?? '');
  const { avgWeekly, lastWeek, availableStock, unit, isFastSale, isSlowSale, isToOrder } =
    getTrendSignals(trend, salesTrendFactor, slowSaleFactor, toOrderFactor);

  return {
    key: articleNumber || `${articleDescription}-${index}`,
    articleNumber,
    articleNumberDisplay: articleNumber || 'N/A',
    articleDescription,
    articleDescriptionDisplay: articleDescription || 'N/A',
    supplierName,
    supplierNameDisplay: supplierName || 'N/A',
    articleSearch: articleNumber.toLowerCase(),
    descriptionSearch: articleDescription.toLowerCase(),
    supplierSearch: supplierName.toLowerCase(),
    avgWeekly,
    lastWeek,
    availableStock,
    unit,
    isFastSale,
    isSlowSale,
    isToOrder,
    formattedAvgWeekly: formatQuantity(avgWeekly),
    formattedLastWeek: formatQuantity(lastWeek),
    formattedAvailableStock: `${formatQuantity(availableStock)} ${unit}`.trim(),
  };
}

function renderFlagPill(isActive, label, tone) {
  if (!isActive) {
    return (
      <Box component="span" sx={FLAG_PILL_EMPTY_SX}>
        -
      </Box>
    );
  }

  return (
    <Box component="span" sx={FLAG_PILL_SX[tone]}>
      {label}
    </Box>
  );
}

const SalesTrendsResultsTable = memo(function SalesTrendsResultsTable({
  rows,
  order,
  orderBy,
  onRequestSort,
  emptyMessage,
}) {
  const renderedRows = useMemo(() => {
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
            {emptyMessage}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => (
      <TableRow key={row.key} hover sx={TABLE_ROW_HOVER_SX}>
        <TableCell>{row.articleNumberDisplay}</TableCell>
        <TableCell>{row.articleDescriptionDisplay}</TableCell>
        <TableCell>{row.supplierNameDisplay}</TableCell>
        <TableCell align="right">{row.formattedAvgWeekly}</TableCell>
        <TableCell align="right">{row.formattedLastWeek}</TableCell>
        <TableCell align="right">{row.formattedAvailableStock}</TableCell>
        <TableCell align="center">{renderFlagPill(row.isToOrder, 'Order', 'error')}</TableCell>
        <TableCell align="center">{renderFlagPill(row.isFastSale, 'Fast', 'primary')}</TableCell>
        <TableCell align="center">{renderFlagPill(row.isSlowSale, 'Slow', 'warning')}</TableCell>
        <TableCell>
          <Box component="input" type="number" defaultValue="0" min="0" sx={QUANTITY_INPUT_SX} />
        </TableCell>
      </TableRow>
    ));
  }, [emptyMessage, rows]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...SURFACE_SX,
        maxHeight: '72vh',
        overflowY: 'auto',
      }}
    >
      <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }} aria-label="sales trends table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '10%' }}>
              <TableSortLabel
                active={orderBy === 'articleNumber'}
                direction={orderBy === 'articleNumber' ? order : 'asc'}
                onClick={() => onRequestSort('articleNumber')}
              >
                Article
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '24%' }}>
              <TableSortLabel
                active={orderBy === 'articleDescription'}
                direction={orderBy === 'articleDescription' ? order : 'asc'}
                onClick={() => onRequestSort('articleDescription')}
              >
                Description
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '18%' }}>
              <TableSortLabel
                active={orderBy === 'supplierName'}
                direction={orderBy === 'supplierName' ? order : 'asc'}
                onClick={() => onRequestSort('supplierName')}
              >
                Supplier
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '9%', textAlign: 'right' }}>
              <TableSortLabel
                active={orderBy === 'averageWeeklySales'}
                direction={orderBy === 'averageWeeklySales' ? order : 'asc'}
                onClick={() => onRequestSort('averageWeeklySales')}
              >
                Avg Qty
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '9%', textAlign: 'right' }}>
              <TableSortLabel
                active={orderBy === 'lastWeekSales'}
                direction={orderBy === 'lastWeekSales' ? order : 'asc'}
                onClick={() => onRequestSort('lastWeekSales')}
              >
                Last Week
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '10%', textAlign: 'right' }}>
              <TableSortLabel
                active={orderBy === 'availableStock'}
                direction={orderBy === 'availableStock' ? order : 'asc'}
                onClick={() => onRequestSort('availableStock')}
              >
                In Stock
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '9%', textAlign: 'center' }}>
              <TableSortLabel
                active={orderBy === 'toOrder'}
                direction={orderBy === 'toOrder' ? order : 'asc'}
                onClick={() => onRequestSort('toOrder')}
              >
                To Order
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '8%', textAlign: 'center' }}>
              <TableSortLabel
                active={orderBy === 'salesTrend'}
                direction={orderBy === 'salesTrend' ? order : 'asc'}
                onClick={() => onRequestSort('salesTrend')}
              >
                Fast
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '8%', textAlign: 'center' }}>
              <TableSortLabel
                active={orderBy === 'slowSale'}
                direction={orderBy === 'slowSale' ? order : 'asc'}
                onClick={() => onRequestSort('slowSale')}
              >
                Slow
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '10%' }}>Quantity</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderedRows}</TableBody>
      </Table>
    </TableContainer>
  );
});

export default function SalesTrendsPage({ connected, setConnected, setError }) {
  const [weeksInput, setWeeksInput] = useState(String(DEFAULT_WEEKS));
  const [appliedWeeks, setAppliedWeeks] = useState(DEFAULT_WEEKS);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [salesTrendFactorInput, setSalesTrendFactorInput] = useState(
    String(DEFAULT_SALES_TREND_FACTOR)
  );
  const [appliedSalesTrendFactor, setAppliedSalesTrendFactor] = useState(
    DEFAULT_SALES_TREND_FACTOR
  );
  const [slowSaleFactorInput, setSlowSaleFactorInput] = useState(
    String(DEFAULT_SLOW_SALE_FACTOR)
  );
  const [appliedSlowSaleFactor, setAppliedSlowSaleFactor] = useState(DEFAULT_SLOW_SALE_FACTOR);
  const [toOrderFactorInput, setToOrderFactorInput] = useState(String(DEFAULT_TO_ORDER_FACTOR));
  const [appliedToOrderFactor, setAppliedToOrderFactor] = useState(DEFAULT_TO_ORDER_FACTOR);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('articleNumber');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [articleFilter, setArticleFilter] = useState('');
  const deferredArticleFilter = useDeferredValue(articleFilter);
  const deferredDescriptionFilter = useDeferredValue(descriptionFilter);
  const deferredSupplierFilter = useDeferredValue(supplierFilter);

  const weeksValidation = validateWeeksInput(weeksInput);
  const salesTrendFactorValidation = validateThresholdInput(
    salesTrendFactorInput,
    'Fast factor'
  );
  const slowSaleFactorValidation = validateThresholdInput(slowSaleFactorInput, 'Slow factor');
  const toOrderFactorValidation = validateThresholdInput(toOrderFactorInput, 'Order factor');
  const thresholdValidationErrors = [
    salesTrendFactorValidation.error,
    slowSaleFactorValidation.error,
    toOrderFactorValidation.error,
  ].filter(Boolean);
  const hasActiveFilters = Boolean(
    deferredArticleFilter.trim() || deferredDescriptionFilter.trim() || deferredSupplierFilter.trim()
  );

  const handleWeeksInputChange = (nextValue) => {
    setWeeksInput(nextValue);
    const validation = validateWeeksInput(nextValue);

    if (!validation.error) {
      setAppliedWeeks(validation.value);
    }
  };

  const handleSalesTrendFactorInputChange = (nextValue) => {
    setSalesTrendFactorInput(nextValue);
    const validation = validateThresholdInput(nextValue, 'Fast factor');

    if (!validation.error) {
      setAppliedSalesTrendFactor(validation.value);
    }
  };

  const handleSlowSaleFactorInputChange = (nextValue) => {
    setSlowSaleFactorInput(nextValue);
    const validation = validateThresholdInput(nextValue, 'Slow factor');

    if (!validation.error) {
      setAppliedSlowSaleFactor(validation.value);
    }
  };

  const handleToOrderFactorInputChange = (nextValue) => {
    setToOrderFactorInput(nextValue);
    const validation = validateThresholdInput(nextValue, 'Order factor');

    if (!validation.error) {
      setAppliedToOrderFactor(validation.value);
    }
  };

  const handleSearch = async () => {
    if (!connected) {
      setError('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      return;
    }

    if (weeksValidation.error) {
      setError(weeksValidation.error);
      return;
    }

    if (thresholdValidationErrors.length > 0) {
      setError('Please correct the threshold values before loading data.');
      return;
    }

    const requestedWeeks = weeksValidation.value;
    const queryParams = new URLSearchParams({ weeks: String(requestedWeeks) });

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/sales-trends?${queryParams.toString()}`);

      if (!response.ok) {
        let errorMessage;

        if (response.status === 401) {
          errorMessage =
            'Failed to load sales trends: Unauthorized. Please reconnect to Fortnox and try again.';
          setConnected(false);
        } else if (response.status === 503) {
          errorMessage = 'Fortnox cannot handle requests at the moment. Please wait.';
          setConnected(true);
        } else {
          errorMessage = 'Failed to fetch sales trends. Please try again.';
          setConnected(false);
        }

        setError(errorMessage);
        return;
      }

      const data = await response.json();
      const nextTrends = Array.isArray(data) ? data : [];

      setTrends(nextTrends);
      setHasFetchedData(true);
      setConnected(true);
    } catch (error) {
      console.error('Failed to load sales trends:', error);
      setError('Unable to load sales trends right now. Please try again.');
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const normalizedArticleFilter = deferredArticleFilter.trim().toLowerCase();
  const normalizedDescriptionFilter = deferredDescriptionFilter.trim().toLowerCase();
  const normalizedSupplierFilter = deferredSupplierFilter.trim().toLowerCase();

  const trendRows = useMemo(
    () =>
      Array.isArray(trends)
        ? trends
            .filter((trend) => trend !== null && trend !== undefined)
            .map((trend, index) =>
              buildTrendRow(
                trend,
                index,
                appliedSalesTrendFactor,
                appliedSlowSaleFactor,
                appliedToOrderFactor
              )
            )
        : [],
    [trends, appliedSalesTrendFactor, appliedSlowSaleFactor, appliedToOrderFactor]
  );

  const filteredRows = useMemo(() => {
    if (
      !normalizedArticleFilter &&
      !normalizedDescriptionFilter &&
      !normalizedSupplierFilter
    ) {
      return trendRows;
    }

    return trendRows.filter((row) => {
      const matchesArticle =
        !normalizedArticleFilter || row.articleSearch.includes(normalizedArticleFilter);
      const matchesDescription =
        !normalizedDescriptionFilter ||
        row.descriptionSearch.includes(normalizedDescriptionFilter);
      const matchesSupplier =
        !normalizedSupplierFilter || row.supplierSearch.includes(normalizedSupplierFilter);

      return matchesArticle && matchesDescription && matchesSupplier;
    });
  }, [
    trendRows,
    normalizedArticleFilter,
    normalizedDescriptionFilter,
    normalizedSupplierFilter,
  ]);

  const sortedRows = useMemo(() => {
    const compareFlags = (flagA, flagB) => (
      order === 'asc' ? Number(flagB) - Number(flagA) : Number(flagA) - Number(flagB)
    );

    const comparator = (rowA, rowB) => {
      switch (orderBy) {
        case 'articleNumber':
          return order === 'asc'
            ? rowA.articleSearch.localeCompare(rowB.articleSearch)
            : rowB.articleSearch.localeCompare(rowA.articleSearch);
        case 'articleDescription':
          return order === 'asc'
            ? rowA.descriptionSearch.localeCompare(rowB.descriptionSearch)
            : rowB.descriptionSearch.localeCompare(rowA.descriptionSearch);
        case 'supplierName':
          return order === 'asc'
            ? rowA.supplierSearch.localeCompare(rowB.supplierSearch)
            : rowB.supplierSearch.localeCompare(rowA.supplierSearch);
        case 'averageWeeklySales':
          return order === 'asc'
            ? rowA.avgWeekly - rowB.avgWeekly
            : rowB.avgWeekly - rowA.avgWeekly;
        case 'lastWeekSales':
          return order === 'asc'
            ? rowA.lastWeek - rowB.lastWeek
            : rowB.lastWeek - rowA.lastWeek;
        case 'availableStock':
          return order === 'asc'
            ? rowA.availableStock - rowB.availableStock
            : rowB.availableStock - rowA.availableStock;
        case 'salesTrend':
          return compareFlags(rowA.isFastSale, rowB.isFastSale);
        case 'slowSale':
          return compareFlags(rowA.isSlowSale, rowB.isSlowSale);
        case 'toOrder':
          return compareFlags(rowA.isToOrder, rowB.isToOrder);
        default:
          return 0;
      }
    };

    return [...filteredRows].sort(comparator);
  }, [filteredRows, order, orderBy]);

  const today = dayjs();
  const lastWeekStart = today.subtract(7, 'day').format('YYYY-MM-DD');
  const lastWeekEnd = today.subtract(1, 'day').format('YYYY-MM-DD');
  const averageStart = today.subtract(appliedWeeks * 7, 'day').format('YYYY-MM-DD');
  const averageEnd = today.subtract(1, 'day').format('YYYY-MM-DD');
  const hasTrendData = trendRows.length > 0;
  const hasFilteredResults = sortedRows.length > 0;
  const isApplyingFilters =
    articleFilter !== deferredArticleFilter ||
    descriptionFilter !== deferredDescriptionFilter ||
    supplierFilter !== deferredSupplierFilter;

  let emptyTableMessage = 'No sales trends data found. Click "Load Data" to fetch data.';
  if (hasFetchedData && hasTrendData && hasActiveFilters) {
    emptyTableMessage = 'No articles match the current filters.';
  } else if (hasFetchedData) {
    emptyTableMessage = 'No sales trends data found for the selected period.';
  }

  const handleExportToExcel = () => {
    if (thresholdValidationErrors.length > 0) {
      setError('Please correct the threshold values before exporting data.');
      return;
    }

    if (!hasFilteredResults) {
      setError('No data to export');
      return;
    }

    try {
      const exportData = sortedRows.map((row) => ({
        Article: row.articleNumber,
        Description: row.articleDescription,
        'Supplier Name': row.supplierName,
        'To Order': row.isToOrder ? 'YES' : 'NO',
        'Avg Qty (wk)': row.avgWeekly,
        'Last Wk Qty': row.lastWeek,
        'In Stock': row.formattedAvailableStock,
        'Fast Sales': row.isFastSale ? 'YES' : 'NO',
        'Slow Sales': row.isSlowSale ? 'YES' : 'NO',
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      worksheet['!cols'] = [
        { wch: 12 },
        { wch: 30 },
        { wch: 20 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Trends');
      XLSX.writeFile(workbook, `sales_trends_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data to Excel');
    }
  };

  return (
    <ThemeProvider theme={SALES_TRENDS_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BACKGROUND,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'rgba(0,151,167,0.14)',
                      color: pageColors.summary,
                    }}
                  >
                    <QueryStatsIcon fontSize="large" />
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" sx={{ color: pageColors.summary, letterSpacing: 3 }}>
                      SALES TRENDS
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                      Sales Trends
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                      Review recent demand changes so you can spot fast movers, slow movers, and
                      order pressure earlier.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Filters and thresholds
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Filter articles, adjust threshold rules, refresh the current period, and
                    export the filtered result.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1fr) auto',
                    },
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        alignItems: 'start',
                      }}
                    >
                      <TextField
                        label="Article #"
                        value={articleFilter}
                        onChange={(event) => setArticleFilter(event.target.value)}
                        size="small"
                        fullWidth
                        sx={TEXT_FILTER_FIELD_SX}
                      />
                      <TextField
                        label="Article Description"
                        value={descriptionFilter}
                        onChange={(event) => setDescriptionFilter(event.target.value)}
                        size="small"
                        fullWidth
                        sx={TEXT_FILTER_FIELD_SX}
                      />
                      <TextField
                        label="Supplier"
                        value={supplierFilter}
                        onChange={(event) => setSupplierFilter(event.target.value)}
                        size="small"
                        fullWidth
                        sx={TEXT_FILTER_FIELD_SX}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        alignItems: 'start',
                      }}
                    >
                      <TextField
                        label="Weeks"
                        type="number"
                        value={weeksInput}
                        onChange={(event) => handleWeeksInputChange(event.target.value)}
                        size="small"
                        error={Boolean(weeksValidation.error)}
                        helperText={weeksValidation.error || `${MIN_WEEKS}-${MAX_WEEKS} weeks`}
                        inputProps={{ min: MIN_WEEKS, max: MAX_WEEKS }}
                        fullWidth
                        sx={SMALL_NUMBER_FIELD_SX}
                      />
                      <TextField
                        label="Fast Factor"
                        type="number"
                        value={salesTrendFactorInput}
                        onChange={(event) =>
                          handleSalesTrendFactorInputChange(event.target.value)
                        }
                        size="small"
                        error={Boolean(salesTrendFactorValidation.error)}
                        inputProps={{ min: 0.1, step: 0.1 }}
                        fullWidth
                        sx={SMALL_NUMBER_FIELD_SX}
                      />
                      <TextField
                        label="Slow Factor"
                        type="number"
                        value={slowSaleFactorInput}
                        onChange={(event) => handleSlowSaleFactorInputChange(event.target.value)}
                        size="small"
                        error={Boolean(slowSaleFactorValidation.error)}
                        inputProps={{ min: 0.1, step: 0.1 }}
                        fullWidth
                        sx={SMALL_NUMBER_FIELD_SX}
                      />
                      <TextField
                        label="Order Factor"
                        type="number"
                        value={toOrderFactorInput}
                        onChange={(event) => handleToOrderFactorInputChange(event.target.value)}
                        size="small"
                        error={Boolean(toOrderFactorValidation.error)}
                        inputProps={{ min: 0.1, step: 0.1 }}
                        fullWidth
                        sx={SMALL_NUMBER_FIELD_SX}
                      />
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          flex: { sm: '0 0 auto' },
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSearch}
                          disabled={loading}
                          sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                        >
                          {loading ? 'Loading...' : 'Load Data'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportToExcel}
                          disabled={loading || !hasFilteredResults}
                          sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                        >
                          Export
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{
                      width: { xs: '100%', lg: 'auto' },
                      justifySelf: { lg: 'end' },
                    }}
                  >
                    <Box
                      sx={{
                        ...FILTER_SUMMARY_ITEM_SX,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 190 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: pageColors.summary,
                        }}
                      >
                        Last Week Sales
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {lastWeekStart} to {lastWeekEnd}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        ...FILTER_SUMMARY_ITEM_SX,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 210 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: pageColors.summary,
                        }}
                      >
                        Average Sales Window
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {averageStart} to {averageEnd}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Fast sale: last week &gt;= {appliedSalesTrendFactor}x average weekly sales. Slow
                  sale: stock &gt;= {appliedSlowSaleFactor}x average weekly sales. To order:
                  average weekly sales &gt; {appliedToOrderFactor}x stock.
                </Typography>
                {thresholdValidationErrors.length > 0 ? (
                  <Typography variant="caption" color="error.main">
                    Correct the highlighted thresholds before loading or exporting. Each factor
                    must be 0.1 or higher.
                  </Typography>
                ) : null}
                {isApplyingFilters ? (
                  <Typography variant="caption" color="text.secondary">
                    Updating filtered results...
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            {loading && (
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: 0, overflow: 'hidden' }}>
                <LinearProgress />
              </Paper>
            )}

            {!loading && !hasFetchedData && (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                No sales trends data loaded yet. Use the filters above and click &quot;Load
                Data&quot;.
              </Alert>
            )}

            {!loading && hasFetchedData && !hasTrendData && (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                No sales trends data found for the selected period.
              </Alert>
            )}

            {!loading && hasTrendData && !hasFilteredResults && (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                No articles match the current filters.
              </Alert>
            )}

            <SalesTrendsResultsTable
              rows={sortedRows}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              emptyMessage={emptyTableMessage}
            />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
